import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FiUserCheck, FiUserX, FiInbox, FiHash, FiChevronLeft, FiChevronRight, FiSearch, FiX, FiMessageCircle } from 'react-icons/fi';
import BASE_URL from '../../../config/config';
import DtDropdown from './DtDropdown';

const DEPARTAMENTOS = [
  { value: 'no_especificado', label: 'Sin Especificar' },
  { value: 'diseno', label: 'Diseño' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'desarrollo', label: 'Desarrollo' },
  { value: 'redes_sociales', label: 'Redes Sociales' },
];

const ESTADOS = [
  { value: 'abierto', label: 'Abierto' },
  { value: 'cerrado', label: 'Cerrado' },
  { value: 'retenido', label: 'Retenido' },
];

const TICKETS_PER_PAGE = 5;

/**
 * Cuenta mensajes de staff (admin/técnico) acumulados desde el último
 * mensaje del propio usuario al final del chat (aún no vistos por él).
 */
const contarMensajesNuevos = (chatRaw) => {
  let chat = chatRaw;
  if (typeof chat === 'string') {
    try {
      chat = JSON.parse(chat);
    } catch (err) {
      return 0;
    }
  }
  if (!Array.isArray(chat)) return 0;
  let count = 0;
  for (let i = chat.length - 1; i >= 0; i -= 1) {
    if (chat[i].role === 'user') break;
    count += 1;
  }
  return count;
};

/**
 * Dashboard para clientes: bienvenida + historial de tickets propios
 * con búsqueda, filtro por departamento y paginación.
 */
const DashboardUsuario = ({ tokenInfo, onLinkClick }) => {
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState('');
  const [selectedDept, setSelectedDept] = useState('');
  const [selectedEstado, setSelectedEstado] = useState('');

  const hayFiltrosActivos = searchText !== '' || selectedDept !== '' || selectedEstado !== '';

  useEffect(() => {
    if (!tokenInfo.usuario) return;
    const fetchTickets = async () => {
      setIsLoading(true);
      try {
        const resp = await axios.get(`${BASE_URL}/tickets/total/${tokenInfo.usuario}`);
        if (resp.status === 200) {
          const sorted = resp.data.tickets.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          setTickets(sorted);
        }
      } catch (err) {
        console.error('Error al obtener los tickets:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchTickets();
  }, [tokenInfo.usuario]);

  useEffect(() => {
    const filtered = tickets.filter(
      (ticket) =>
        (ticket.asunto.toLowerCase().includes(searchText.toLowerCase()) ||
          ticket.identificador.toString().includes(searchText)) &&
        (selectedDept === '' || ticket.dpto.toLowerCase() === selectedDept.toLowerCase()) &&
        (selectedEstado === '' || ticket.estado_del_ticket.toLowerCase() === selectedEstado.toLowerCase())
    );
    setFilteredTickets(filtered);
    setCurrentPage(1);
  }, [searchText, tickets, selectedDept, selectedEstado]);

  const limpiarFiltros = () => {
    setSearchText('');
    setSelectedDept('');
    setSelectedEstado('');
  };

  const totalPages = Math.ceil(filteredTickets.length / TICKETS_PER_PAGE);
  const indexOfLast = currentPage * TICKETS_PER_PAGE;
  const indexOfFirst = indexOfLast - TICKETS_PER_PAGE;
  const currentTickets = filteredTickets.slice(indexOfFirst, indexOfLast);

  return (
    <div className="dash-panel">
      <div className="dash-welcome">
        <h1>¡Hola, {tokenInfo.nombre}!</h1>
        <p>Nos alegramos de verte de nuevo por aquí. Consulta tus tickets y habla con un técnico en tiempo real.</p>
      </div>

      <div className="dt-filtros-bar">
        <div className="dt-search">
          <FiSearch className="dt-search-icon" />
          <input
            type="text"
            className="dt-search-input"
            placeholder="Búsqueda por identificador o asunto..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>
        <DtDropdown
          options={DEPARTAMENTOS}
          value={selectedDept}
          onChange={setSelectedDept}
          placeholder="Todos los departamentos"
        />
        <DtDropdown
          options={ESTADOS}
          value={selectedEstado}
          onChange={setSelectedEstado}
          placeholder="Todos los estados"
        />
        {hayFiltrosActivos && (
          <button type="button" className="dt-filtros-limpiar" onClick={limpiarFiltros}>
            <FiX /> Limpiar
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="dash-tickets-loading">Cargando tus tickets...</div>
      ) : (
        <div className="dt-table-card">
          <div className="dt-table-header">
            <span>Ticket</span>
            <span>Estado</span>
            <span>Departamento</span>
            <span>Prioridad</span>
            <span>Fecha</span>
            <span>Asignado a</span>
          </div>
          <div className="dt-ticket-list">
            {filteredTickets.length === 0 ? (
              <div className="dt-empty-state">
                <FiInbox />
                {hayFiltrosActivos ? (
                  <>
                    <p>Ningún ticket coincide con los filtros aplicados.</p>
                    <button type="button" className="dt-empty-reset" onClick={limpiarFiltros}>Limpiar filtros</button>
                  </>
                ) : (
                  <p>¡No tienes tickets creados todavía!</p>
                )}
              </div>
            ) : (
              currentTickets.map((ticket) => {
                const mensajesNuevos = ticket.estado_del_chat === 'respondido' && ticket.estado_del_ticket !== 'cerrado'
                  ? contarMensajesNuevos(ticket.chat)
                  : 0;
                return (
                <Link
                  key={ticket.identificador}
                  className="dt-ticket-row"
                  to={`/ticket/${ticket.identificador}`}
                  onClick={onLinkClick}
                >
                  <div className="dt-col-ticket">
                    <div className="dt-ticket-icon"><FiHash /></div>
                    <div className="dt-col-ticket-texto">
                      <span className="dt-ticket-id">#{ticket.identificador}</span>
                      <span className="dt-ticket-asunto">
                        {ticket.asunto.split(' ').slice(0, 6).join(' ')}
                        {ticket.asunto.split(' ').length > 6 ? '...' : ''}
                      </span>
                    </div>
                    {mensajesNuevos > 0 && (
                      <span className="dt-mensajes-nuevos" title={`${mensajesNuevos} mensaje${mensajesNuevos > 1 ? 's' : ''} nuevo${mensajesNuevos > 1 ? 's' : ''}`}>
                        <FiMessageCircle />
                        {mensajesNuevos}
                      </span>
                    )}
                  </div>

                  <div className="dt-col-badges">
                    <div className="dt-col-estado">
                      {ticket.estado_del_ticket === 'abierto' && <p className="abierto">Abierto</p>}
                      {ticket.estado_del_ticket === 'cerrado' && <p className="cerrado">Cerrado</p>}
                      {ticket.estado_del_ticket === 'retenido' && <p className="retenido">Retenido</p>}
                    </div>
                    <div className="dt-col-dpto">
                      {ticket.dpto === 'no_especificado' && <p className="no-especificado">Sin especificar</p>}
                      {ticket.dpto === 'desarrollo' && <p className="desarrollo">Desarrollo</p>}
                      {ticket.dpto === 'diseno' && <p className="diseno">Diseño</p>}
                      {ticket.dpto === 'marketing' && <p className="marketing">Marketing</p>}
                      {ticket.dpto === 'redes_sociales' && <p className="redes-sociales">Redes Sociales</p>}
                    </div>
                    <div className="dt-col-prioridad">
                      {ticket.prioridad === 'no_urgente' && <p className="sin-prioridad">No urgente</p>}
                      {ticket.prioridad === 'urgente' && <p className="alta">Urgente</p>}
                    </div>
                  </div>

                  <div className="dt-col-fecha">{new Date(ticket.createdAt).toLocaleDateString()}</div>
                  <div className="dt-col-asignado">
                    {ticket.usuario_encargado === 'sin_asignar' ? (
                      <>
                        <span className="dt-asignado-icono dt-asignado-icono-vacio"><FiUserX /></span>
                        <p>Sin asignar</p>
                      </>
                    ) : (
                      <>
                        <span className="dt-asignado-icono dt-asignado-icono-activo"><FiUserCheck /></span>
                        <p>@{ticket.usuario_encargado}</p>
                      </>
                    )}
                  </div>
                </Link>
                );
              })
            )}
          </div>
          {filteredTickets.length > TICKETS_PER_PAGE && (
            <div className="dt-pagination">
              <button
                type="button"
                className="dt-pagination-btn"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                aria-label="Página anterior"
              >
                <FiChevronLeft />
              </button>
              <span className="dt-pagination-info">Página {currentPage} de {totalPages}</span>
              <button
                type="button"
                className="dt-pagination-btn"
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                aria-label="Página siguiente"
              >
                <FiChevronRight />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DashboardUsuario;
